import React, { useState } from "react";
import {
  RSVP_FORM_URL,
  CONTACT_EMAIL,
  EMAILJS_SERVICE_ID,
  EMAILJS_TEMPLATE_ID,
  EMAILJS_PUBLIC_KEY,
} from "../config";

const translations = {
  en: {
    heading: "RSVP",
    firstName: "First name",
    lastName: "Last name",
    email: "Your email",
    additionalGuestsLabel: "Additional guests :",
    additionalGuestName: "Guest name (optional)",
    willAttend: "Will you be attending?",
    yes: "Yes",
    no: "No",
    whichEvents: "Which events will you be joining?",
    geetGawai: "Geet Gawai - July 28th",
    haldi: "Haldi - July 29th",
    wedding: "Wedding - July 29th",
    reception: "Reception - July 30th",
    dietary: "Allergies / Dietary restrictions?",
    dietaryPlaceholder: "Please tell us your allergies / dietary restrictions",
    messagePlaceholder: "A message (optional)",
    submitBtn: "Submit RSVP",
    confirmationMsg: "Thank you! Your RSVP was sent 😊",
    errorFirstName: "First name is required.",
    errorLastName: "Last name is required.",
    errorEmail: "Email is required.",
    errorAttending: "Please select whether you are attending.",
    errorGuests: "Please provide a name for each guest.",
    errorEvents: "Please choose at least one event.",
    errorDietary: "Please tell us your allergies / dietary restrictions.",
    errorSubmit: "There was an error sending your RSVP. Please try again.",
    sending: "Sending...",
    maxGuests: "You can add up to 10 additional guests.",
  },
  fr: {
    heading: "RSVP",
    firstName: "Prénom",
    lastName: "Nom",
    email: "Votre email",
    additionalGuestsLabel: "Invités supplémentaires :",
    additionalGuestName: "Nom de l'invité (optionnel)",
    willAttend: "Serez-vous présent(e) ?",
    yes: "Oui",
    no: "Non",
    whichEvents: "À quels événements allez-vous assister ?",
    geetGawai: "Geet Gawai - 28 juillet",
    haldi: "Haldi - 29 juillet",
    wedding: "Mariage - 29 juillet",
    reception: "Réception - 30 juillet",
    dietary: "Allergies / Restrictions alimentaires ?",
    dietaryPlaceholder: "Veuillez nous indiquer vos allergies / restrictions alimentaires",
    messagePlaceholder: "Un message (optionnel)",
    submitBtn: "Envoyer RSVP",
    confirmationMsg: "Merci ! Votre RSVP a été envoyé 😊",
    errorFirstName: "Le prénom est requis.",
    errorLastName: "Le nom est requis.",
    errorEmail: "L'email est requis.",
    errorAttending: "Veuillez indiquer si vous participez.",
    errorGuests: "Veuillez fournir un nom pour chaque invité.",
    errorEvents: "Veuillez choisir au moins un événement.",
    errorDietary: "Veuillez nous indiquer vos allergies / restrictions alimentaires.",
    errorSubmit: "Une erreur s'est produite lors de l'envoi de votre RSVP. Réessayez.",
    sending: "Envoi...",
    maxGuests: "Vous pouvez ajouter jusqu'à 10 invités supplémentaires.",
  },
};

export default function RSVPForm() {
  const [lang, setLang] = useState("en");
  const t = translations[lang];
  
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [guests, setGuests] = useState([]);
  const [guestCount, setGuestCount] = useState(0);
  const [attending, setAttending] = useState("");
  const [events, setEvents] = useState([]);
  const [dietary, setDietary] = useState("no");
  const [dietaryDetails, setDietaryDetails] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();

    setStatus("");
    setConfirmation("");

    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedEmail = email.trim();

    const validationErrors = {};

    if (!trimmedFirstName) {
      validationErrors.firstName = t.errorFirstName;
    }
    if (!trimmedLastName) {
      validationErrors.lastName = t.errorLastName;
    }
    if (!trimmedEmail) {
      validationErrors.email = t.errorEmail;
    }
    if (!attending) {
      validationErrors.attending = t.errorAttending;
    }


    if (attending === "yes") {
      if (events.length === 0) {
        validationErrors.events = t.errorEvents;
      }

      if (dietary === "yes" && !dietaryDetails.trim()) {
        validationErrors.dietary = t.errorDietary;
      }
    }

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);

    const fullName = `${trimmedFirstName} ${trimmedLastName}`;
    const guestList = guests.map((g) => g.trim()).filter(Boolean).join(", ");
    const eventsText = events.join(", ");
    const dietaryRestrictions = dietary === "yes" ? dietaryDetails.trim() : "";

    const payload = {
      name: fullName,
      email: trimmedEmail,
      guestCount,
      guests: guestList,
      attending,
      events: eventsText,
      dietaryRestrictions,
      message: message.trim(),
    };

    const formData = new URLSearchParams(payload);

    const sendEmailNotification = async () => {
      if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
        return;
      }

      await fetch("https://api.emailjs.com/api/v1.0/email/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          service_id: EMAILJS_SERVICE_ID,
          template_id: EMAILJS_TEMPLATE_ID,
          user_id: EMAILJS_PUBLIC_KEY,
          template_params: {
            to_email: CONTACT_EMAIL,
            subject: `New RSVP from ${payload.name}`,
            from_name: payload.name,
            from_email: payload.email,
            guestCount: payload.guestCount,
            guests: payload.guests,
            attending: payload.attending,
            events: payload.events,
            dietary: payload.dietaryRestrictions,
            message: payload.message,
          }
        }),
      });
    };

    try {
      await Promise.all([
        fetch(RSVP_FORM_URL, {
          method: "POST",
          body: formData,
          mode: "no-cors",
        }),
        sendEmailNotification().catch((err) => {
          console.warn("RSVP email notification failed:", err);
        }),
      ]);

      setConfirmation(t.confirmationMsg);

      setFirstName("");
      setLastName("");
      setEmail("");
      setGuests([]);
      setGuestCount(0);
      setAttending("");
      setEvents([]);
      setDietary("no");
      setDietaryDetails("");
      setMessage("");
      setErrors({});
    } catch (err) {
      setStatus(t.errorSubmit);
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section 
      id="rsvp" 
      className="min-h-screen flex flex-col items-center justify-center px-4 py-16 relative"
      style={{
        backgroundImage: "url('/Images/RSVP/RSVP_background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="bg-white bg-opacity-90 p-4 md:p-6 rounded-lg z-10 w-full max-w-lg">
        <div className="relative w-full max-w-md mx-auto mb-4 z-10">
          <h2 id="rsvp-heading" className="text-3xl text-center">{t.heading}</h2>
          <div className="absolute right-0 top-1/2 -translate-y-1/2">
            <label htmlFor="language-select" className="sr-only">Language</label>
            <select
              id="language-select"
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="border rounded px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              aria-label="Select language"
            >
              <option value="en">EN</option>
              <option value="fr">FR</option>
            </select>
          </div>
        </div>

        {confirmation ? (
          <div className="relative w-full max-w-md mx-auto rounded-lg border border-amber-300 bg-amber-50 p-6 text-amber-900">
            <p className="font-medium">{confirmation}</p>
            <button
              type="button"
              className="absolute right-3 top-3 text-amber-700 hover:text-amber-900"
              onClick={() => setConfirmation("")}
              aria-label="Close confirmation"
            >
              ✕
            </button>
          </div>
        ) : (
          <form className="max-w-md w-full mx-auto space-y-4" onSubmit={handleSubmit} aria-labelledby="rsvp-heading">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="sr-only" htmlFor="firstName">
                  {t.firstName}
                </label>
                <input
                  id="firstName"
                  type="text"
                  placeholder={t.firstName}
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setErrors((prev) => ({ ...prev, firstName: "" }));
                  }}
                  className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  required
                  aria-required="true"
                />
                {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
              </div>
              <div>
                <label className="sr-only" htmlFor="lastName">
                  {t.lastName}
                </label>
                <input
                  id="lastName"
                  type="text"
                  placeholder={t.lastName}
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    setErrors((prev) => ({ ...prev, lastName: "" }));
                  }}
                  className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  required
                  aria-required="true"
                />
                {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
              </div>
            </div>

            <div>
              <label className="sr-only" htmlFor="email">
                {t.email}
              </label>
              <input
                id="email"
                type="email"
                placeholder={t.email}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors((prev) => ({ ...prev, email: "" }));
                }}
                className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                required
                aria-required="true"
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-700 font-medium">{t.additionalGuestsLabel}</p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (guestCount <= 0) return;
                      const newCount = guestCount - 1;
                      setGuestCount(newCount);
                      setGuests((prev) => prev.slice(0, newCount));
                      setErrors((prev) => ({ ...prev, guests: "" }));
                    }}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-amber-400 bg-white text-amber-700 shadow-sm transition hover:bg-amber-100 disabled:opacity-50"
                    aria-label="Decrease guests"
                  >
                    −
                  </button>
                  <div className="w-12 text-center text-lg font-semibold text-gray-900">{guestCount}</div>
                  <button
                    type="button"
                    onClick={() => {
                      if (guestCount >= 10) {
                        setErrors((prev) => ({ ...prev, guests: t.maxGuests }));
                        return;
                      }
                      setGuestCount((c) => c + 1);
                      setGuests((prev) => [...prev, ""]);
                      setErrors((prev) => ({ ...prev, guests: "" }));
                    }}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-amber-400 bg-white text-amber-700 shadow-sm transition hover:bg-amber-100 disabled:opacity-50"
                    aria-label="Increase guests"
                  >
                    +
                  </button>
                </div>
              </div>
              {guestCount > 0 && (
                <div className="space-y-3 pl-4 border-l border-amber-200">
                  {Array.from({ length: guestCount }).map((_, idx) => (
                    <input
                      key={idx}
                      type="text"
                      placeholder={t.additionalGuestName}
                      value={guests[idx] || ""}
                      onChange={(e) => {
                        const updated = [...guests];
                        updated[idx] = e.target.value;
                        setGuests(updated);
                        setErrors((prev) => ({ ...prev, guests: "" }));
                      }}
                      className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                  ))}
                </div>
              )}
              {errors.guests && <p className="text-sm text-red-600">{errors.guests}</p>}
            </div>

            <div className="space-y-2">
              <p className="font-medium">{t.willAttend}</p>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="attendance"
                    value="yes"
                    checked={attending === "yes"}
                    onChange={(e) => {
                      setAttending(e.target.value);
                      setErrors((prev) => ({ ...prev, attending: "" }));
                    }}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-400 border-gray-300"
                  />
                  {t.yes}
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="attendance"
                    value="no"
                    checked={attending === "no"}
                    onChange={(e) => {
                      setAttending(e.target.value);
                      setErrors((prev) => ({ ...prev, attending: "" }));
                    }}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-400 border-gray-300"
                  />
                  {t.no}
                </label>
              </div>
              {errors.attending && <p className="text-sm text-red-600">{errors.attending}</p>}
            </div>

            {attending === "yes" && (
              <>
                <div className="border rounded p-4 bg-white/70">
                  <p className="font-medium mb-2">{t.whichEvents}</p>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { id: "geetgawai", label: t.geetGawai },
                      { id: "haldi", label: t.haldi },
                      { id: "wedding", label: t.wedding },
                      { id: "reception", label: t.reception },
                    ].map((event) => (
                      <label key={event.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={events.includes(event.id)}
                          onChange={(e) => {
                            setEvents((prev) =>
                              e.target.checked ? [...prev, event.id] : prev.filter((v) => v !== event.id)
                            );
                            setErrors((prev) => ({ ...prev, events: "" }));
                          }}
                          className="h-4 w-4 text-amber-600 focus:ring-amber-400 border-gray-300 rounded"
                        />
                        {event.label}
                      </label>
                    ))}
                  </div>
                  {errors.events && <p className="mt-2 text-sm text-red-600">{errors.events}</p>}
                </div>

                <div className="border rounded p-4 bg-white/70">
                  <p className="font-medium mb-2">{t.dietary}</p>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="dietary"
                        value="no"
                        checked={dietary === "no"}
                        onChange={() => {
                          setDietary("no");
                          setErrors((prev) => ({ ...prev, dietary: "" }));
                        }}
                        className="h-4 w-4 text-amber-600 focus:ring-amber-400 border-gray-300"
                      />
                      {t.no}
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="dietary"
                        value="yes"
                        checked={dietary === "yes"}
                        onChange={() => {
                          setDietary("yes");
                          setErrors((prev) => ({ ...prev, dietary: "" }));
                        }}
                        className="h-4 w-4 text-amber-600 focus:ring-amber-400 border-gray-300"
                      />
                      {t.yes}
                    </label>
                  </div>
                  {dietary === "yes" && (
                    <textarea
                      placeholder={t.dietaryPlaceholder}
                      value={dietaryDetails}
                      onChange={(e) => {
                        setDietaryDetails(e.target.value);
                        setErrors((prev) => ({ ...prev, dietary: "" }));
                      }}
                      className="w-full border rounded px-4 py-2 mt-3 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                  )}
                  {errors.dietary && <p className="mt-2 text-sm text-red-600">{errors.dietary}</p>}
                </div>
              </>
            )}

            <div>
              <label className="sr-only" htmlFor="message">
                {t.messagePlaceholder}
              </label>
              <textarea
                id="message"
                placeholder={t.messagePlaceholder}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-amber-600 text-white px-4 py-2 rounded shadow hover:bg-amber-700 transition disabled:opacity-50"
            >
              {submitting ? t.sending : t.submitBtn}
            </button>
            {status && (
              <div role="status" className="text-center text-sm text-gray-700">
                {status}
              </div>
            )}
          </form>
        )}
      </div>
    </section>
  );
}
